import React, {
  FC,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import DownLtLogoBlue from "@svgIcons/DownIt";
import styles from "./programs.module.scss";
import ProgramCard from "./ProgramCard";
interface ProgramsListingProps {
  setMenuPage: (page: number) => void;
  programsDetails: {
    categoryParentTitle: string;
    categoryTitle: string;
    programsList: any[];
  };
  showProgramsListing?: boolean;
  closeMenuHandler?: (href:string) => void;
}

const ProgramsListing: FC<ProgramsListingProps> = ({
  setMenuPage,
  programsDetails,
  showProgramsListing,
  closeMenuHandler,
}) => {
  const [visiblePrograms, setVisiblePrograms] = useState(6);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const previousCategoryRef = useRef<string>(programsDetails.categoryTitle);
  const allLoaded = visiblePrograms >= programsDetails.programsList.length;

  // Reset visible programs when category changes
  useEffect(() => {
    if (previousCategoryRef.current !== programsDetails.categoryTitle) {
      setVisiblePrograms(6); // Reset to initial count
      previousCategoryRef.current = programsDetails.categoryTitle;
    }
  }, [programsDetails.categoryTitle]);

  const goBack = useCallback(() => {
    setMenuPage(1);
  }, [setMenuPage]);

  const visibleProgramsList = useMemo(
    () => programsDetails.programsList.slice(0, visiblePrograms),
    [programsDetails.programsList, visiblePrograms]
  );

  // Intersection Observer to load more items when the observer element is visible
  useEffect(() => {
    // Only set up observer if we haven't loaded all programs
    if (allLoaded) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setVisiblePrograms((prev) =>
            Math.min(prev + 6, programsDetails.programsList.length)
          );
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [visiblePrograms, programsDetails.programsList.length, allLoaded]);

  return (
    <div
      className={`${styles.programsListingMainContainer} ${
        showProgramsListing ? styles.open : ""
      }`}
    >
      <div
        className={styles.arrowContainer}
        onClick={goBack}
      >
        <DownLtLogoBlue className={styles.leftArrow} width={15} height={15} />
        <span className={styles.programListTitle}>
          {programsDetails?.categoryTitle === "KPMG PROGRAM" ? "KPMG" : programsDetails?.categoryTitle}
        </span>
      </div>

      <div className={styles.programsListingWrapper}>
        {visibleProgramsList.map((program, index) => (
          <ProgramCard key={index} program={program} programsDetails={programsDetails} isMobile={true} onCloseHandler={closeMenuHandler}/>
        ))}

        {!allLoaded && (
          <div
            ref={observerRef}
            className={styles.loadingIndicator}
            aria-live="polite"
          >
            Loading more...
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProgramsListing);
